<template>
    <div>
        <div class="demo-spin-article">
            <div class="row mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">{{
                        $t('collateralRecord.loan')
                    }}</label>
                    <ts-loading-banner :loading="loadingLoans">
                        <Select
                            v-model="model.loan_id"
                            filterable
                            remote
                            :remote-method="searchLoans"
                            :loading="searchingLoans"
                            :class="{ 'is-invalid': errors.has('loan_id') }"
                            :placeholder="$t('collateralRecord.searchLoan')"
                        >
                            <Option
                                v-for="loan in loans"
                                :key="loan.loan_id"
                                :value="loan.loan_id"
                            >
                                {{ loan.loan_number }}
                                <span v-if="loan.customer">
                                    - {{ loan.customer.customer_name_en }}
                                </span>
                            </Option>
                        </Select>
                    </ts-loading-banner>
                    <div class="invalid-feedback" v-if="errors.has('loan_id')">
                        {{ errors.first('loan_id') }}
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">{{
                        $t('collateralRecord.collateralAsset')
                    }}</label>
                    <ts-loading-banner :loading="loadingAssets">
                        <select
                            v-model="model.asset_id"
                            class="form-control"
                            :class="{ 'is-invalid': errors.has('asset_id') }"
                        >
                            <option
                                :value="null"
                                disabled
                                selected
                                style="display: none"
                            >
                                {{ $t('select') }}
                            </option>
                            <option
                                v-for="asset in collateralAssets"
                                :key="asset.asset_id"
                                :value="asset.asset_id"
                            >
                                {{ asset.asset_name }} ({{ asset.asset_code }})
                            </option>
                        </select>
                    </ts-loading-banner>
                    <div
                        class="invalid-feedback"
                        v-if="errors.has('asset_id')"
                    >
                        {{ errors.first('asset_id') }}
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-6">
                    <label class="form-col-label control-label required">{{
                        $t('collateralRecord.attachedDate')
                    }}</label>
                    <DatePicker
                        type="date"
                        v-model="model.attached_date"
                        format="dd-MM-yyyy"
                        :class="{ 'is-invalid': errors.has('attached_date') }"
                        style="width: 100%"
                    ></DatePicker>
                    <div
                        class="invalid-feedback"
                        v-if="errors.has('attached_date')"
                    >
                        {{ errors.first('attached_date') }}
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label">{{
                        $t('collateralRecord.notes')
                    }}</label>
                    <textarea
                        v-model="model.notes"
                        rows="3"
                        class="form-control"
                        :class="{ 'is-invalid': errors.has('notes') }"
                    ></textarea>
                    <div class="invalid-feedback" v-if="errors.has('notes')">
                        {{ errors.first('notes') }}
                    </div>
                </div>
            </div>
            <div slot="footer">
                <div class="tw-flex tw-justify-end tw-space-x-2 tw-sh">
                    <ts-button
                        @click.prevent="() => $emit('cancel')"
                        class="btn-gray"
                    >
                        {{ $t('cancel') }}</ts-button
                    >
                    <ts-button
                        color="primary"
                        v-if="!isUpdate"
                        outline
                        :waiting="waiting_new"
                        :disabled="waiting"
                        @click.prevent="onSaveAddNew"
                    >
                        {{ $t('saveAddNew') }}</ts-button
                    >
                    <ts-button
                        color="primary"
                        v-if="!isUpdate"
                        :waiting="waiting"
                        :disabled="waiting_new"
                        @click.prevent="onSave"
                    >
                        {{ $t('save') }}</ts-button
                    >
                    <ts-button
                        v-if="isUpdate"
                        color="primary"
                        @click.prevent="onUpdate"
                        :waiting="waiting"
                    >
                        {{ $t('update') }}</ts-button
                    >
                </div>
            </div>
        </div>
        <Spin size="large" fix v-if="loading"> </Spin>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState, mapActions } from 'vuex'
import { isEmpty, debounce } from 'lodash'
import moment from 'moment'

export default {
    name: 'collateralRecordForm',
    data () {
        return {
            errors: new Errors(),
            loading: false,
            loadingAssets: false,
            loadingLoans: false,
            searchingLoans: false,
            waiting: false,
            waiting_new: false,
            model: {
                loan_id: null,
                asset_id: null,
                attached_date: new Date(),
                attachment_info: null,
                notes: null
            }
        }
    },
    computed: {
        ...mapState('creditOperation/collateralRecord', [
            'edit_data',
            'collateralAssets',
            'loans'
        ]),
        isUpdate () {
            return !isEmpty(this.edit_data)
        }
    },
    methods: {
        ...mapActions('creditOperation/collateralRecord', [
            'getCollateralAssets',
            'getLoans'
        ]),
        async fetchResource () {
            this.loading = true
            this.loadingAssets = true
            this.loadingLoans = true
            await this.getCollateralAssets()
            this.loadingAssets = false
            await this.getLoans()
            this.loadingLoans = false
            this.setEditData()
            this.loading = false
        },
        searchLoans: debounce(function (query) {
            if (query !== '') {
                this.searchingLoans = true
                this.getLoans({ search: query }).finally(() => {
                    this.searchingLoans = false
                })
            }
        }, 300),
        formatDate (date) {
            if (!date) return null
            return moment(date).format('DD-MM-YYYY')
        },
        onSaveAddNew () {
            this.errors = new Errors()
            this.waiting_new = true
            const data = {
                ...this.model,
                attached_date: this.formatDate(this.model.attached_date)
            }
            this.$store
                .dispatch('creditOperation/collateralRecord/store', data)
                .then(response => {
                    this.notice({ type: 'success', text: response.message })
                    this.clearInput()
                    this.$emit('fetchData')
                })
                .catch(error => {
                    this.errors = new Errors(error.errors)
                    this.notice({ type: 'error', text: error.message })
                })
                .finally(() => {
                    this.waiting_new = false
                })
        },
        onSave () {
            this.errors = new Errors()
            this.waiting = true
            const data = {
                ...this.model,
                attached_date: this.formatDate(this.model.attached_date)
            }
            this.$store
                .dispatch('creditOperation/collateralRecord/store', data)
                .then(response => {
                    this.notice({ type: 'success', text: response.message })
                    this.$emit('cancel')
                    this.$emit('fetchData')
                })
                .catch(error => {
                    this.errors = new Errors(error.errors)
                    this.notice({ type: 'error', text: error.message })
                })
                .finally(() => {
                    this.waiting = false
                })
        },
        onUpdate () {
            this.waiting = true
            const data = {
                ...this.model,
                attached_date: this.formatDate(this.model.attached_date)
            }
            this.$store
                .dispatch('creditOperation/collateralRecord/update', {
                    id: this.edit_data.collateral_id,
                    data: data
                })
                .then(response => {
                    this.notice({ type: 'success', text: response.message })
                    this.$emit('cancel')
                    this.$emit('fetchData')
                })
                .catch(error => {
                    this.errors = new Errors(error.errors)
                    this.notice({ type: 'error', text: error.message })
                })
                .finally(() => {
                    this.waiting = false
                })
        },
        setEditData () {
            if (!isEmpty(this.edit_data)) {
                this.model.loan_id = this.edit_data.loan_id
                this.model.asset_id = this.edit_data.asset_id
                this.model.attached_date = this.edit_data.attached_date
                    ? moment(this.edit_data.attached_date, 'DD-MM-YYYY').toDate()
                    : new Date()
                this.model.attachment_info = this.edit_data.attachment_info
                this.model.notes = this.edit_data.notes
            }
        },
        clearInput () {
            this.model.loan_id = null
            this.model.asset_id = null
            this.model.attached_date = new Date()
            this.model.attachment_info = null
            this.model.notes = null
        },
        notice (not) {
            this.$Notice[not.type]({
                title: 'COLLATERAL RECORD',
                desc: not.text
            })
        }
    },
    watch: {
        edit_data: {
            handler () {
                this.fetchResource()
            },
            immediate: true
        }
    }
}
</script>
